import { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useInsightsStore, PostInsights } from '../store/insights';
import { api } from '../services/api';
import { PageHeader } from '../components/PageHeader';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

// History data types
interface HistorySnapshot {
  instagramPostId: string;
  snapshotDate: string;
  caption: string | null;
  mediaType: string;
  thumbnailUrl: string | null;
  postTimestamp: string;
  ageInDays: number;
  reach: number;
  likeCount: number;
  commentsCount: number;
  saved: number;
  shares: number;
  engagementRate: number;
  weightedEngagement: number;
  qualityScore: number;
  reachVelocity: number;
  engagementVelocity: number;
  earlyPerformanceScore: number;
  performanceCategory: string;
}

interface DailyDelta {
  instagramPostId: string;
  snapshotDate: string;
  caption: string | null;
  mediaType: string;
  thumbnailUrl: string | null;
  postTimestamp: string;
  ageInDays: number;
  reach: number;
  likeCount: number;
  commentsCount: number;
  saved: number;
  shares: number;
  engagementRate: number;
  qualityScore: number;
  reachDelta: number;
  likesDelta: number;
  commentsDelta: number;
  savedDelta: number;
  sharesDelta: number;
  isFirstSnapshot: boolean;
}

interface HistoryStats {
  totalSnapshots: number;
  uniquePosts: number;
  dateRange: { earliest: string | null; latest: string | null };
  snapshotsPerDay: { date: string; count: number }[];
}

// KPI type definition
type KpiKey = 'reach' | 'likeCount' | 'commentsCount' | 'saved' | 'shares' | 'engagementRate' | 'qualityScore' | 'reachVelocity' | 'earlyPerformanceScore';

// KPI Configuration with categories
interface KpiConfig {
  color: string;
  unit: string;
  category: 'basic' | 'early';
  deltaKey: string | null;
}

const kpiConfigData: Record<KpiKey, KpiConfig> = {
  // From KPI Trends - cumulative values
  reach: { color: '#3B82F6', unit: '', category: 'basic', deltaKey: 'reachDelta' },
  likeCount: { color: '#EC4899', unit: '', category: 'basic', deltaKey: 'likesDelta' },
  commentsCount: { color: '#8B5CF6', unit: '', category: 'basic', deltaKey: 'commentsDelta' },
  saved: { color: '#10B981', unit: '', category: 'basic', deltaKey: 'savedDelta' },
  shares: { color: '#F59E0B', unit: '', category: 'basic', deltaKey: 'sharesDelta' },
  engagementRate: { color: '#EF4444', unit: '%', category: 'basic', deltaKey: null },
  qualityScore: { color: '#6366F1', unit: '', category: 'basic', deltaKey: null },
  // From Fruehphase
  reachVelocity: { color: '#14B8A6', unit: '/h', category: 'early', deltaKey: null },
  earlyPerformanceScore: { color: '#F97316', unit: '', category: 'early', deltaKey: null },
};

// Color palette for posts
const postColors = [
  '#3B82F6', // blue
  '#EF4444', // red
  '#10B981', // green
  '#F59E0B', // amber
  '#8B5CF6', // purple
  '#EC4899', // pink
  '#14B8A6', // teal
  '#F97316', // orange
  '#6366F1', // indigo
  '#84CC16', // lime
];

// Custom Tooltip
function CustomTooltip({
  active,
  payload,
  label,
  selectedPosts,
  showDeltas,
}: {
  active?: boolean;
  payload?: readonly { value: number; dataKey: string; color: string; name: string }[];
  label?: string | number;
  selectedPosts: PostInsights[];
  showDeltas: boolean;
}) {
  if (!active || !payload || !payload.length) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 max-w-sm">
      <p className="text-sm font-medium text-gray-900 mb-2">{String(label)}</p>
      <div className="space-y-2">
        {payload.map((entry, index) => {
          const postId = entry.dataKey.split('_')[0];
          const post = selectedPosts.find(p => p.id === postId);
          return (
            <div key={index} className="flex items-center gap-2">
              {post?.thumbnailUrl && (
                <img src={post.thumbnailUrl} alt="" className="w-6 h-6 rounded object-cover" />
              )}
              <span className="text-xs text-gray-600 truncate max-w-[120px]" title={post?.caption || ''}>
                {post?.caption?.slice(0, 20) || 'Post'}
              </span>
              <span className="ml-auto font-medium text-sm" style={{ color: entry.color }}>
                {showDeltas && entry.value > 0 ? '+' : ''}
                {typeof entry.value === 'number'
                  ? entry.value.toLocaleString('de-DE', { maximumFractionDigits: 2 })
                  : entry.value}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AnalyticsCompare() {
  const { t } = useTranslation(['analytics', 'common']);

  // Get KPI label dynamically
  const getKpiLabel = (kpi: KpiKey): string => {
    const labels: Record<KpiKey, string> = {
      reach: t('analytics:metrics.reachShort'),
      likeCount: t('analytics:metrics.likes'),
      commentsCount: t('analytics:metrics.comments'),
      saved: t('analytics:metrics.saves'),
      shares: t('analytics:metrics.shares'),
      engagementRate: t('analytics:metrics.engagementRateShort'),
      qualityScore: t('analytics:metrics.qualityScore'),
      reachVelocity: t('analytics:metrics.velocity'),
      earlyPerformanceScore: t('analytics:metrics.perfScore'),
    };
    return labels[kpi];
  };

  // Get category label dynamically
  const getCategoryLabel = (category: string): string => {
    const labels: Record<string, string> = {
      exceptional: t('analytics:earlyStage.categories.exceptional'),
      above_average: t('analytics:earlyStage.categories.above_average'),
      average: t('analytics:earlyStage.categories.average'),
      below_average: t('analytics:earlyStage.categories.below_average'),
      new: t('analytics:earlyStage.categories.new'),
    };
    return labels[category] || category;
  };

  const {
    posts,
    account,
    isLoading,
    error,
    fetchInsights,
  } = useInsightsStore();

  const [selectedPostIds, setSelectedPostIds] = useState<string[]>([]);
  const [selectedKpi, setSelectedKpi] = useState<KpiKey>('reach');
  const [maxDays, setMaxDays] = useState(30);
  const [showDeltas, setShowDeltas] = useState(true); // Show daily changes vs cumulative

  // History data state
  const [historyStats, setHistoryStats] = useState<HistoryStats | null>(null);
  const [historyData, setHistoryData] = useState<HistorySnapshot[]>([]);
  const [deltaData, setDeltaData] = useState<DailyDelta[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isCollecting, setIsCollecting] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  useEffect(() => {
    fetchInsights(50);
    loadHistoryStats();
  }, [fetchInsights]);

  // Load history stats
  const loadHistoryStats = async () => {
    try {
      const response = await api.get<{ success: boolean; stats: HistoryStats }>('/insights/history/stats');
      if (response.success) {
        setHistoryStats(response.stats);
      }
    } catch (err) {
      console.error('Failed to load history stats:', err);
    }
  };

  // Load history data when posts are selected
  const loadHistoryData = useCallback(async (postIds: string[]) => {
    if (postIds.length === 0) {
      setHistoryData([]);
      setDeltaData([]);
      return;
    }

    setIsLoadingHistory(true);
    setHistoryError(null);

    try {
      const [historyResponse, deltaResponse] = await Promise.all([
        api.get<{ success: boolean; data: HistorySnapshot[] }>(
          `/insights/history?postIds=${postIds.join(',')}&days=${maxDays}`
        ),
        api.get<{ success: boolean; data: DailyDelta[] }>(
          `/insights/deltas?postIds=${postIds.join(',')}&days=${maxDays}`
        ),
      ]);

      if (historyResponse.success) {
        setHistoryData(historyResponse.data || []);
      }
      if (deltaResponse.success) {
        setDeltaData(deltaResponse.data || []);
      }
    } catch (err) {
      setHistoryError(err instanceof Error ? err.message : t('analytics:error.loadHistoryFailed'));
    } finally {
      setIsLoadingHistory(false);
    }
  }, [maxDays, t]);

  // Load history when selection changes
  useEffect(() => {
    if (selectedPostIds.length > 0) {
      loadHistoryData(selectedPostIds);
    }
  }, [selectedPostIds, loadHistoryData]);

  // Collect insights now
  const collectInsightsNow = async () => {
    setIsCollecting(true);
    try {
      const response = await api.post<{ success: boolean; savedCount: number; message: string }>(
        '/insights/history/collect'
      );
      if (response.success) {
        alert(`${t('common:success')}: ${response.message}`);
        await loadHistoryStats();
        if (selectedPostIds.length > 0) {
          await loadHistoryData(selectedPostIds);
        }
      }
    } catch (err) {
      alert(`${t('common:error')}: ` + (err instanceof Error ? err.message : t('common:somethingWentWrong')));
    } finally {
      setIsCollecting(false);
    }
  };

  // Auto-select first 3 posts when loaded
  useEffect(() => {
    if (posts.length > 0 && selectedPostIds.length === 0) {
      const recentPosts = posts
        .filter(p => p.ageInDays <= maxDays)
        .sort((a, b) => a.ageInDays - b.ageInDays)
        .slice(0, 3);
      setSelectedPostIds(recentPosts.map(p => p.id));
    }
  }, [posts, selectedPostIds.length, maxDays]);

  // Filter posts within maxDays
  const availablePosts = useMemo(() =>
    posts.filter(p => p.ageInDays <= maxDays).sort((a, b) => a.ageInDays - b.ageInDays),
    [posts, maxDays]
  );

  const selectedPosts = useMemo(() =>
    availablePosts.filter(p => selectedPostIds.includes(p.id)),
    [availablePosts, selectedPostIds]
  );

  // Check if we have history data
  const hasHistoryData = historyData.length > 0 || deltaData.length > 0;

  // Get KPI info
  const kpiInfo = kpiConfigData[selectedKpi];

  // Build chart data from history or simulate
  const chartData = useMemo(() => {
    if (selectedPosts.length === 0) return [];

    // If we have history data, use it
    if (hasHistoryData && (showDeltas ? deltaData.length > 0 : historyData.length > 0)) {
      const dataSource = showDeltas ? deltaData : historyData;

      // Group by date
      const dateMap = new Map<string, Record<string, number>>();

      for (const snapshot of dataSource) {
        const dateStr = new Date(snapshot.snapshotDate).toLocaleDateString('de-DE', {
          day: '2-digit',
          month: '2-digit',
        });

        if (!dateMap.has(dateStr)) {
          dateMap.set(dateStr, { date: Date.parse(snapshot.snapshotDate) });
        }

        const point = dateMap.get(dateStr)!;

        // Get the value - for deltas use delta key, for cumulative use direct key
        let value: number;
        if (showDeltas && kpiInfo.deltaKey && snapshot.hasOwnProperty(kpiInfo.deltaKey)) {
          value = (snapshot as unknown as Record<string, number>)[kpiInfo.deltaKey] || 0;
        } else {
          value = (snapshot as unknown as Record<string, number>)[selectedKpi] || 0;
        }

        point[`${snapshot.instagramPostId}_${selectedKpi}`] = value;
      }

      // Convert to array and sort by date
      return Array.from(dateMap.entries())
        .map(([dateStr, values]) => {
          const dateNum = values.date;
          return { ...values, dateLabel: dateStr, sortDate: dateNum };
        })
        .sort((a, b) => a.sortDate - b.sortDate);
    }

    // Fallback: simulate data based on current values
    const maxAge = Math.max(...selectedPosts.map(p => p.ageInDays));
    const data: Array<{ day: number; [key: string]: number }> = [];

    for (let day = 0; day <= maxAge; day++) {
      const point: { day: number; [key: string]: number } = { day };

      for (const post of selectedPosts) {
        if (day <= post.ageInDays) {
          const kpiValue = post[selectedKpi as keyof PostInsights];
          if (typeof kpiValue === 'number') {
            const growthFactor = day / post.ageInDays;
            point[`${post.id}_${selectedKpi}`] = Math.round(kpiValue * growthFactor * 100) / 100;
          }
        }
      }

      data.push(point);
    }

    return data;
  }, [selectedPosts, selectedKpi, hasHistoryData, historyData, deltaData, showDeltas, kpiInfo.deltaKey]);

  // Simple bar comparison data (current values)
  const currentComparisonData = useMemo(() => {
    return selectedPosts.map((post, index) => ({
      id: post.id,
      name: post.caption?.slice(0, 30) || `Post ${index + 1}`,
      thumbnail: post.thumbnailUrl,
      value: post[selectedKpi as keyof PostInsights] as number,
      color: postColors[index % postColors.length],
      ageInDays: post.ageInDays,
      post,
    }));
  }, [selectedPosts, selectedKpi]);

  const togglePost = (postId: string) => {
    setSelectedPostIds(prev =>
      prev.includes(postId)
        ? prev.filter(id => id !== postId)
        : prev.length < 10
          ? [...prev, postId]
          : prev
    );
  };

  const selectAll = () => {
    setSelectedPostIds(availablePosts.slice(0, 10).map(p => p.id));
  };

  const deselectAll = () => {
    setSelectedPostIds([]);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader
          icon={
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
            </svg>
          }
          title={t('analytics:postComparison')}
          subtitle={account ? `@${account.username} • ${t('analytics:comparePostsDirectly')}` : t('analytics:loadingShort')}
          breadcrumbs={[
            { label: t('analytics:breadcrumbs.marketing'), href: '/marketing' },
            { label: t('analytics:breadcrumbs.analytics'), href: '/marketing/analytics' },
            { label: t('analytics:breadcrumbs.compare') },
          ]}
        />

        {/* History Status Banner */}
        <div className="mt-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h3 className="font-medium text-blue-900">{t('analytics:compare.insightsHistory')}</h3>
              {historyStats ? (
                <p className="text-sm text-blue-700">
                  {historyStats.totalSnapshots > 0 ? (
                    <>
                      {historyStats.totalSnapshots} {t('analytics:compare.snapshotsOf')} {historyStats.uniquePosts} {t('analytics:compare.posts')}
                      {historyStats.dateRange.earliest && historyStats.dateRange.latest && (
                        <> • {new Date(historyStats.dateRange.earliest).toLocaleDateString('de-DE')} bis {new Date(historyStats.dateRange.latest).toLocaleDateString('de-DE')}</>
                      )}
                    </>
                  ) : (
                    t('analytics:compare.noHistoryYet')
                  )}
                </p>
              ) : (
                <p className="text-sm text-blue-600">{t('analytics:compare.loadingStatus')}</p>
              )}
            </div>
            <button
              onClick={collectInsightsNow}
              disabled={isCollecting}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isCollecting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {t('analytics:compare.collecting')}
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {t('analytics:compare.collectNow')}
                </>
              )}
            </button>
          </div>
          {!hasHistoryData && historyStats && historyStats.totalSnapshots === 0 && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>{t('common:info')}:</strong> {t('analytics:compare.historyNote')}
              </p>
            </div>
          )}
        </div>

        {error && (
          <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {historyError && (
          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-amber-800">{historyError}</p>
          </div>
        )}

        {isLoading ? (
          <div className="mt-8 flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            {/* Controls Row */}
            <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="flex flex-wrap items-center gap-4">
                {/* KPI Selector */}
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">{t('analytics:compare.kpiLabel')}</label>
                  <select
                    value={selectedKpi}
                    onChange={(e) => setSelectedKpi(e.target.value as KpiKey)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <optgroup label={t('analytics:kpiCategories.basic')}>
                      {(Object.keys(kpiConfigData) as KpiKey[])
                        .filter(key => kpiConfigData[key].category === 'basic')
                        .map(key => (
                          <option key={key} value={key}>{getKpiLabel(key)}</option>
                        ))}
                    </optgroup>
                    <optgroup label={t('analytics:kpiCategories.early')}>
                      {(Object.keys(kpiConfigData) as KpiKey[])
                        .filter(key => kpiConfigData[key].category === 'early')
                        .map(key => (
                          <option key={key} value={key}>{getKpiLabel(key)}</option>
                        ))}
                    </optgroup>
                  </select>
                </div>

                {/* Days Filter */}
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">{t('analytics:compare.periodLabel')}</label>
                  <select
                    value={maxDays}
                    onChange={(e) => setMaxDays(Number(e.target.value))}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={7}>{t('analytics:compare.last7Days')}</option>
                    <option value={14}>{t('analytics:compare.last14Days')}</option>
                    <option value={21}>{t('analytics:compare.last21Days')}</option>
                    <option value={30}>{t('analytics:compare.last30Days')}</option>
                    <option value={60}>{t('analytics:compare.last60Days')}</option>
                  </select>
                </div>

                {/* Show Deltas Toggle */}
                {hasHistoryData && kpiInfo.deltaKey && (
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">{t('analytics:compare.displayLabel')}</label>
                    <button
                      onClick={() => setShowDeltas(!showDeltas)}
                      className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
                        showDeltas
                          ? 'bg-green-100 text-green-700 border border-green-300'
                          : 'bg-gray-100 text-gray-700 border border-gray-300'
                      }`}
                    >
                      {showDeltas ? t('analytics:compare.dailyChange') : t('analytics:compare.cumulative')}
                    </button>
                  </div>
                )}

                {/* Selection Buttons */}
                <div className="flex items-center gap-2 ml-auto">
                  <button
                    onClick={selectAll}
                    className="px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                  >
                    {t('analytics:compare.selectAll')}
                  </button>
                  <button
                    onClick={deselectAll}
                    className="px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                  >
                    {t('analytics:compare.deselectAll')}
                  </button>
                </div>
              </div>
            </div>

            {/* Post Selector */}
            <div className="mt-4 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                {t('analytics:compare.selectPosts')} ({selectedPostIds.length}/10 {t('analytics:compare.postsSelected')})
              </h3>
              <div className="flex flex-wrap gap-2">
                {availablePosts.map((post) => {
                  const isSelected = selectedPostIds.includes(post.id);
                  const colorIndex = selectedPostIds.indexOf(post.id);
                  const color = colorIndex >= 0 ? postColors[colorIndex % postColors.length] : '#9CA3AF';

                  return (
                    <button
                      key={post.id}
                      onClick={() => togglePost(post.id)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all ${
                        isSelected
                          ? 'bg-gray-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      style={{ borderColor: isSelected ? color : undefined }}
                    >
                      {post.thumbnailUrl && (
                        <img
                          src={post.thumbnailUrl}
                          alt=""
                          className="w-8 h-8 rounded object-cover"
                        />
                      )}
                      <div className="text-left">
                        <p className="text-xs font-medium text-gray-900 truncate max-w-[120px]">
                          {post.caption?.slice(0, 20) || t('analytics:noText')}
                        </p>
                        <p className="text-xs text-gray-500">{t('analytics:compare.daysOld', { days: post.ageInDays })}</p>
                      </div>
                      {isSelected && (
                        <div
                          className="w-3 h-3 rounded-full ml-1"
                          style={{ backgroundColor: color }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Current Values Comparison */}
            {currentComparisonData.length > 0 && (
              <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {t('analytics:compare.currentComparison')}: {getKpiLabel(selectedKpi)}
                </h3>

                {/* Bar Chart Style Comparison */}
                <div className="space-y-3">
                  {currentComparisonData
                    .sort((a, b) => b.value - a.value)
                    .map((item, index) => {
                      const maxValue = Math.max(...currentComparisonData.map(d => d.value));
                      const percentage = maxValue > 0 ? (item.value / maxValue) * 100 : 0;

                      return (
                        <div key={item.id} className="flex items-center gap-3">
                          <div className="flex items-center gap-2 w-48 flex-shrink-0">
                            <span className="text-lg font-bold text-gray-400 w-6">#{index + 1}</span>
                            {item.thumbnail && (
                              <img src={item.thumbnail} alt="" className="w-10 h-10 rounded object-cover" />
                            )}
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate" title={item.post.caption || ''}>
                                {item.name}
                              </p>
                              <p className="text-xs text-gray-500">{t('analytics:compare.daysOld', { days: item.ageInDays })}</p>
                            </div>
                          </div>
                          <div className="flex-1 flex items-center gap-3">
                            <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all duration-500"
                                style={{
                                  width: `${percentage}%`,
                                  backgroundColor: item.color,
                                }}
                              />
                            </div>
                            <span className="text-sm font-bold w-20 text-right" style={{ color: item.color }}>
                              {item.value.toLocaleString('de-DE', { maximumFractionDigits: 2 })}{kpiInfo.unit}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            {/* Line Chart Over Time */}
            {chartData.length > 0 && selectedPosts.length > 0 && (
              <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {showDeltas && hasHistoryData && kpiInfo.deltaKey
                        ? `${t('analytics:compare.dailyChangeTitle')}: ${getKpiLabel(selectedKpi)}`
                        : t('analytics:compare.developmentOverTime', { kpi: getKpiLabel(selectedKpi) })}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {hasHistoryData
                        ? showDeltas && kpiInfo.deltaKey
                          ? t('analytics:compare.showsDailyChange')
                          : t('analytics:compare.showsCumulativeValues')
                        : t('analytics:compare.showsSimulatedData')}
                    </p>
                  </div>
                  {isLoadingHistory && (
                    <div className="flex items-center gap-2 text-blue-600">
                      <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                      <span className="text-sm">{t('analytics:compare.loadingHistory')}</span>
                    </div>
                  )}
                </div>

                <div style={{ height: 400 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={chartData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis
                        dataKey={hasHistoryData ? 'dateLabel' : 'day'}
                        tick={{ fill: '#6B7280', fontSize: 12 }}
                        label={{
                          value: hasHistoryData ? t('analytics:compare.date') : t('analytics:compare.daysSincePosting'),
                          position: 'insideBottom',
                          offset: -10,
                          fill: '#6B7280',
                        }}
                      />
                      <YAxis
                        tick={{ fill: '#6B7280', fontSize: 12 }}
                        tickFormatter={(value) =>
                          value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value.toString()
                        }
                      />
                      <Tooltip
                        content={(props) => (
                          <CustomTooltip {...props} selectedPosts={selectedPosts} showDeltas={showDeltas && hasHistoryData} />
                        )}
                      />
                      <Legend
                        formatter={(value) => {
                          const postId = value.split('_')[0];
                          const post = selectedPosts.find(p => p.id === postId);
                          return post?.caption?.slice(0, 25) || 'Post';
                        }}
                      />

                      {selectedPosts.map((post, index) => (
                        <Line
                          key={post.id}
                          type="monotone"
                          dataKey={`${post.id}_${selectedKpi}`}
                          name={`${post.id}_${selectedKpi}`}
                          stroke={postColors[index % postColors.length]}
                          strokeWidth={2}
                          dot={{ r: 3 }}
                          activeDot={{ r: 5 }}
                          connectNulls
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Detailed Comparison Table */}
            {selectedPosts.length > 0 && (
              <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-200">
                  <h3 className="font-semibold text-gray-900">{t('analytics:compare.detailComparison')}</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('analytics:compare.metric')}</th>
                        {selectedPosts.map((post, index) => (
                          <th key={post.id} className="px-4 py-3 text-right text-xs font-medium uppercase" style={{ color: postColors[index % postColors.length] }}>
                            <div className="flex items-center justify-end gap-2">
                              {post.thumbnailUrl && (
                                <img src={post.thumbnailUrl} alt="" className="w-6 h-6 rounded object-cover" />
                              )}
                              <span className="truncate max-w-[80px]">{post.caption?.slice(0, 15) || 'Post'}</span>
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {(Object.keys(kpiConfigData) as KpiKey[]).map(key => (
                        <tr key={key} className={key === selectedKpi ? 'bg-blue-50' : 'hover:bg-gray-50'}>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            {getKpiLabel(key)}
                            {key === selectedKpi && (
                              <span className="ml-2 text-xs text-blue-600">{t('analytics:compare.selected')}</span>
                            )}
                          </td>
                          {selectedPosts.map((post, index) => {
                            const value = post[key as keyof PostInsights];
                            return (
                              <td key={post.id} className="px-4 py-3 text-right text-sm">
                                <span style={{ color: postColors[index % postColors.length] }}>
                                  {typeof value === 'number'
                                    ? value.toLocaleString('de-DE', { maximumFractionDigits: 2 })
                                    : value}
                                  {kpiConfigData[key].unit}
                                </span>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                      <tr className="bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{t('analytics:compare.age')}</td>
                        {selectedPosts.map((post, index) => (
                          <td key={post.id} className="px-4 py-3 text-right text-sm">
                            <span style={{ color: postColors[index % postColors.length] }}>
                              {post.ageInDays} {t('analytics:compare.days')}
                            </span>
                          </td>
                        ))}
                      </tr>
                      <tr className="bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{t('common:status')}</td>
                        {selectedPosts.map((post, index) => {
                          return (
                            <td key={post.id} className="px-4 py-3 text-right text-sm">
                              <span
                                className="px-2 py-1 rounded-full text-xs"
                                style={{
                                  backgroundColor: `${postColors[index % postColors.length]}20`,
                                  color: postColors[index % postColors.length],
                                }}
                              >
                                {getCategoryLabel(post.performanceCategory)}
                              </span>
                            </td>
                          );
                        })}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {selectedPosts.length === 0 && (
              <div className="mt-6 bg-gray-50 rounded-xl p-12 text-center">
                <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 mb-2">{t('analytics:compare.noPostsSelected')}</h3>
                <p className="text-gray-500">{t('analytics:compare.selectPostsToCompare')}</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
